import { useEffect, useState, type ReactNode } from 'react';
import {
  DEFAULT_LEARNING_SETTINGS,
  type SmartFillUiPreferences
} from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { InlineError, SidebarState } from '../components/SidebarState';
import { useKeyedBridgeAction } from '../sidebar/data';
import { displayError, type DisplayError } from '../bridge/errors';
import { useLearningSession } from '../sidebar/learning';

export function SettingsView() {
  const learning = useLearningSession();
  const learningSettings = learning.snapshot?.settings ?? DEFAULT_LEARNING_SETTINGS;
  const { client } = useSidebarSession();
  const [preferences, setPreferences] = useState<SmartFillUiPreferences | null>(null);
  const [loadError, setLoadError] = useState<DisplayError | null>(null);
  const save = useKeyedBridgeAction<keyof SmartFillUiPreferences>();
  const saving = save.busy;
  const error = loadError ?? save.error;

  useEffect(() => {
    let active = true;
    void client
      .request('settings.get', {})
      .then((value) => {
        if (active) setPreferences(value);
      })
      .catch((requestError) => {
        if (active) setLoadError(displayError(requestError, 'Could not load Smart Fill settings.'));
      });
    return () => {
      active = false;
    };
  }, [client]);

  const update = async (key: keyof SmartFillUiPreferences, checked: boolean) => {
    if (!preferences) return;
    await save.run(key, 'Could not save this setting.', async () => {
      setPreferences(await client.request('settings.update', { [key]: checked }));
    });
  };

  if (!preferences && !error) {
    return <SidebarState compact kind="loading" title="Loading settings" />;
  }

  return (
    <section className="settings-view" aria-labelledby="settings-heading">
      <div className="section-heading">
        <h1 id="settings-heading">Settings</h1>
      </div>
      {error && <InlineError message={error.message} />}
      {preferences && (
        <SettingsGroup id="settings-display" title="Display">
          <PreferenceToggle
            label="Page buttons"
            description="Show the Smart Fill buttons on webpages."
            checked={preferences.showPageButtons}
            disabled={saving !== null}
            onChange={(checked) => void update('showPageButtons', checked)}
          />
          <PreferenceToggle
            label="Bottom bar"
            description="Show fill progress at the bottom of webpages."
            checked={preferences.showBottomBar}
            disabled={saving !== null}
            onChange={(checked) => void update('showBottomBar', checked)}
          />
          <PreferenceToggle
            label="Technical problems"
            description="Explain fields and fills that failed for reasons you can't fix, for troubleshooting."
            checked={preferences.showTechnicalProblems === true}
            disabled={saving !== null}
            onChange={(checked) => void update('showTechnicalProblems', checked)}
          />
        </SettingsGroup>
      )}
      <SettingsGroup id="settings-learning" title="Learning during fill">
        <PreferenceToggle
          label="Learn from what you type"
          description="Learn which bucket field a question needs from answers you type. No correction needed."
          checked={learningSettings.learnFromFormEdits}
          disabled={!learning.snapshot || learning.settingsBusy}
          onChange={(checked) => void learning.updateSettings({ learnFromFormEdits: checked })}
        />
        <PreferenceToggle
          label="Save missing information"
          description="During continuous fill, add answers you type that the bucket is missing."
          checked={learningSettings.saveMissingInformation}
          disabled={!learning.snapshot || learning.settingsBusy}
          onChange={(checked) => void learning.updateSettings({ saveMissingInformation: checked })}
        />
      </SettingsGroup>
      {learning.error && <InlineError message={learning.error.message} />}
      {!learning.snapshot && learning.error && (
        <button type="button" className="button button--quiet" onClick={learning.reload}>
          Retry learning settings
        </button>
      )}
    </section>
  );
}

function SettingsGroup({
  id,
  title,
  children
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id}>
      <div className="section-heading">
        <h2 id={id}>{title}</h2>
      </div>
      <div className="settings-list">{children}</div>
    </section>
  );
}

function PreferenceToggle({
  label,
  description,
  checked,
  disabled,
  onChange
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="preference-toggle">
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}
